import React, { useCallback, useEffect, useState } from 'react';
import { useTranslate, useNotify } from 'react-admin';
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Chip,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Card,
    CardContent,
} from '@mui/material';
import { Settings as SettingsIcon, Edit as EditIcon, Clear } from '@mui/icons-material';
import { apiClient } from '../../utils/apiClient';
import { API_CONFIG } from '../../config/api';

type KioskSettingScope = 'system' | 'site' | 'kiosk';

interface KioskSettingDefinition {
    key: string;
    authority: 'cloud' | 'device';
    scopes: KioskSettingScope[];
    defaultValue: unknown;
    description: string;
}

interface EffectiveKioskSetting {
    value: unknown;
    version: number;
    authorityClass: 'cloud' | 'device';
    scope: 'default' | KioskSettingScope;
}

interface KioskSettingsPanelProps {
    kioskId: string;
    reloadKey: number;
}

const kioskSettingsErrorHeader = (error: any, translate: (key: string, options?: any) => string): string => {
    const apiError = error?.body || error;
    const kioskErrorMessage = apiError?.errorCode
        ? translate(`resources.kiosk-devices.errors.${apiError.errorCode}`, { _: '' })
        : '';
    return kioskErrorMessage || apiError?.message || translate('validation.formHasErrors', { _: 'Error' });
};

export const KioskSettingsPanel: React.FC<KioskSettingsPanelProps> = ({ kioskId, reloadKey }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [definitions, setDefinitions] = useState<KioskSettingDefinition[]>([]);
    const [effective, setEffective] = useState<Record<string, EffectiveKioskSetting>>({});
    const [loading, setLoading] = useState(true);
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [overrideDialog, setOverrideDialog] = useState<{ open: boolean; key: string; value: string; error: string }>({
        open: false,
        key: '',
        value: '',
        error: '',
    });

    const loadSettings = useCallback(async () => {
        try {
            const response = await apiClient.get<{ data: { cells: any[]; effective: Record<string, EffectiveKioskSetting>; configHash: string } }>(
                `${API_CONFIG.endpoints.backend.kioskDevices}/${kioskId}/settings`
            );
            setEffective(response.data.effective);
        } catch (error: any) {
            notify(kioskSettingsErrorHeader(error, translate), { type: 'error' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kioskId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const definitionsResponse = await apiClient.get<{ data: KioskSettingDefinition[] }>(API_CONFIG.endpoints.backend.kioskSettingDefinitions);
                await loadSettings();
                if (!cancelled) setDefinitions(definitionsResponse.data);
            } catch (error: any) {
                if (!cancelled) notify(kioskSettingsErrorHeader(error, translate), { type: 'error' });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kioskId]);

    useEffect(() => {
        if (reloadKey === 0) return;
        loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadKey]);

    const openOverrideDialog = (key: string) => {
        const current = effective[key];
        setOverrideDialog({ open: true, key, value: JSON.stringify(current?.value ?? null), error: '' });
    };

    const closeOverrideDialog = () => setOverrideDialog({ open: false, key: '', value: '', error: '' });

    const handleSaveOverride = async () => {
        let parsed: unknown;
        try {
            parsed = JSON.parse(overrideDialog.value);
        } catch {
            setOverrideDialog((s) => ({ ...s, error: translate('resources.kiosk-devices.settings.invalid') }));
            return;
        }
        setBusyKey(overrideDialog.key);
        try {
            await apiClient.put(`${API_CONFIG.endpoints.backend.kioskDevices}/${kioskId}/settings/${encodeURIComponent(overrideDialog.key)}`, { value: parsed });
            notify(translate('resources.kiosk-devices.settings.saved'), { type: 'success' });
            closeOverrideDialog();
            await loadSettings();
        } catch (error: any) {
            notify(kioskSettingsErrorHeader(error, translate), { type: 'error' });
        } finally {
            setBusyKey(null);
        }
    };

    const handleClearOverride = async (key: string) => {
        setBusyKey(key);
        try {
            await apiClient.delete(`${API_CONFIG.endpoints.backend.kioskDevices}/${kioskId}/settings/${encodeURIComponent(key)}`);
            notify(translate('resources.kiosk-devices.settings.cleared'), { type: 'success' });
            await loadSettings();
        } catch (error: any) {
            notify(kioskSettingsErrorHeader(error, translate), { type: 'error' });
        } finally {
            setBusyKey(null);
        }
    };

    return (
        <Card sx={{ overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <SettingsIcon sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {translate('resources.kiosk-devices.settings.title')}
                    </Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>{translate('resources.kiosk-devices.settings.key')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{translate('resources.kiosk-devices.settings.value')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{translate('resources.kiosk-devices.settings.source')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{translate('resources.kiosk-devices.settings.authority')}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {definitions.map((definition) => {
                                    const cell = effective[definition.key];
                                    const allowsKiosk = definition.scopes.includes('kiosk');
                                    const isBusy = busyKey === definition.key;
                                    return (
                                        <TableRow key={definition.key} hover>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{definition.key}</Typography>
                                                <Typography variant="caption" color="text.secondary">{definition.description}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                    {cell ? JSON.stringify(cell.value) : '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" label={translate(`resources.kiosk-devices.settings.scope.${cell?.scope || 'default'}`)} />
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" variant="outlined" label={translate(`resources.kiosk-devices.settings.authorityClass.${cell?.authorityClass || definition.authority}`)} />
                                            </TableCell>
                                            <TableCell align="right">
                                                {!allowsKiosk ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {translate('resources.kiosk-devices.settings.kioskScopeNotAllowed')}
                                                    </Typography>
                                                ) : isBusy ? (
                                                    <CircularProgress size={18} />
                                                ) : (
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                                        <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => openOverrideDialog(definition.key)} sx={{ textTransform: 'none' }}>
                                                            {translate('resources.kiosk-devices.settings.override')}
                                                        </Button>
                                                        {cell?.scope === 'kiosk' && (
                                                            <IconButton size="small" onClick={() => handleClearOverride(definition.key)} title={translate('resources.kiosk-devices.settings.clear')}>
                                                                <Clear fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Dialog open={overrideDialog.open} onClose={closeOverrideDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>{translate('resources.kiosk-devices.settings.override')}</DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{overrideDialog.key}</Typography>
                            <TextField
                                value={overrideDialog.value}
                                onChange={(e) => setOverrideDialog((s) => ({ ...s, value: e.target.value, error: '' }))}
                                multiline
                                minRows={3}
                                fullWidth
                                error={Boolean(overrideDialog.error)}
                                helperText={overrideDialog.error || translate('resources.kiosk-devices.settings.jsonHint')}
                                sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={closeOverrideDialog} disabled={busyKey === overrideDialog.key} sx={{ textTransform: 'none' }}>
                            {translate('ra.action.cancel')}
                        </Button>
                        <Button
                            onClick={handleSaveOverride}
                            variant="contained"
                            disabled={busyKey === overrideDialog.key}
                            sx={{ backgroundColor: '#00C853', '&:hover': { backgroundColor: '#00A843' }, textTransform: 'none', fontWeight: 600 }}
                        >
                            {translate('ra.action.save')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </CardContent>
        </Card>
    );
};
